import styled from 'styled-components';
import { FormattedNumber, FormattedMessage, useIntl } from 'react-intl';
import { color, font, label, ease, bp } from '../../styles/tokens';
import { CONFIG } from '../../consts/prices';
import Reveal from './Reveal';

// Values are stored in BRL. When the active language is set to another
// currency in CONFIG, they're converted at the configured rate.
export function Money({ value, prefix }) {
  const { locale } = useIntl();
  const currency = CONFIG.moedaPorIdioma[locale] || 'BRL';
  const amount = currency === 'BRL' ? value : value / CONFIG.cotacaoDoDolar;

  return (
    <>
      {prefix}
      {/* eslint-disable-next-line react/style-prop-object */}
      <FormattedNumber value={amount} style="currency" currency={currency} />
    </>
  );
}

const richText = { b: (chunks) => <b>{chunks}</b> };

// Renders one row from the price list in src/consts/prices.js.
function itemToRow(item) {
  return {
    name: <FormattedMessage id={item.nome} values={richText} />,
    note: item.nota ? <FormattedMessage id={item.nota} /> : null,
    price: (
      <>
        {item.apartirDe && (
          <>
            <FormattedMessage id="price.from" />{' '}
          </>
        )}
        <Money value={item.preco} prefix={item.mais ? '+ ' : undefined} />
        {item.cada && (
          <>
            {' '}
            <FormattedMessage id="price.each" />
          </>
        )}
      </>
    ),
  };
}

export { itemToRow };

const Card = styled.div`
  background: ${(p) => (p.$bare ? 'transparent' : color.white)};
  border: ${(p) => (p.$bare ? 0 : `1px solid ${color.line}`)};
  border-radius: 14px;
  padding: ${(p) => (p.$bare ? 0 : 'clamp(24px, 4vw, 34px)')};
  height: 100%;

  h3 {
    ${label};
    color: ${color.fuchsia};
    padding-bottom: 14px;
    border-bottom: 1px solid ${color.line};
    margin-bottom: 8px;
  }
`;

const Row = styled.li`
  padding: 14px 0;
  border-bottom: 1px solid ${color.porcelain};

  &:last-child {
    border-bottom: 0;
    padding-bottom: 0;
  }

  .line {
    display: flex;
    align-items: baseline;
    gap: 8px;
  }

  .name {
    font-size: 15px;
    font-weight: 400;
    color: ${color.text};

    b {
      font-weight: 500;
      color: ${color.ink};
    }
  }

  /* Dotted leader — the price-list idiom, kept honest rather than decorative. */
  .leader {
    flex: 1;
    height: 0;
    border-bottom: 1px dotted ${color.line};
    transform: translateY(-4px);
    min-width: 16px;
  }

  .price {
    font-family: ${font.body};
    font-size: 15px;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    color: ${color.ink};
    white-space: nowrap;
  }

  .note {
    font-size: 12.5px;
    line-height: 1.4;
    color: ${color.textMute};
    margin-top: 4px;
  }
`;

export function PriceGroup({ title, items, bare }) {
  const rows = items.map(itemToRow);

  return (
    <Card $bare={bare}>
      {title && <h3>{title}</h3>}
      <ul>
        {rows.map((item, i) => (
          <Row key={i}>
            <div className="line">
              <span className="name">{item.name}</span>
              <i className="leader" aria-hidden="true" />
              <span className="price">{item.price}</span>
            </div>
            {item.note && <p className="note">{item.note}</p>}
          </Row>
        ))}
      </ul>
    </Card>
  );
}

export const PriceGrid = styled.div`
  display: grid;
  gap: 20px;

  ${bp.md} {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
    align-items: start;
  }

  /* Three groups, three columns — anything less leaves a hole beside the last. */
  ${bp.lg} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const Cta = styled.a`
  ${label};
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 15px 30px;
  border-radius: 999px;
  background: ${color.fuchsia};
  color: ${color.white};
  transition: background 0.3s ${ease}, transform 0.3s ${ease};

  &:hover {
    background: ${color.fuchsiaDark};
    transform: translateY(-2px);
  }

  &.ghost {
    background: transparent;
    color: ${color.white};
    border: 1px solid rgba(255, 255, 255, 0.38);

    &:hover {
      background: rgba(255, 255, 255, 0.08);
      border-color: ${color.rose};
      color: ${color.rose};
    }
  }
`;

export default PriceGroup;

export { Reveal };
